import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { submitLead } from "@/lib/api";
import { Loader2, Send, Gift } from "lucide-react";

export default function LeadDialog({ open, onOpenChange, plan = "", title = "Book a Free Consultation", showOffer = true }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please enter your name and email.");
      return;
    }
    setLoading(true);
    try {
      await submitLead({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        message: form.message.trim() || null,
        plan: plan || null,
        source: plan ? "pricing" : "landing",
      });
      toast.success("Thanks! Our growth team will reach out within 24 hours.");
      setForm({ name: "", email: "", phone: "", message: "" });
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="border-white/10 bg-slate-950/95 backdrop-blur-xl sm:max-w-md"
        data-testid="lead-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-white">
            {title}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {plan
              ? `Great choice — the ${plan} plan. Share your details and we'll get you started.`
              : "Tell us about your business. A specialist will reach out within 24 hours."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="mt-2 space-y-4" data-testid="lead-form">
          {/* Free leads banner */}
          {showOffer && (
          <div
            className="relative overflow-hidden rounded-xl border border-emerald-400/40 bg-gradient-to-r from-emerald-500/15 via-emerald-500/10 to-teal-500/15 p-3"
            data-testid="free-leads-banner"
          >
            <div className="pointer-events-none absolute -right-6 -top-6 h-20 w-20 rounded-full bg-emerald-400/20 blur-2xl" aria-hidden />
            <div className="relative flex items-start gap-3">
              <span className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-emerald-500/20 ring-1 ring-emerald-400/40">
                <Gift className="h-4 w-4 text-emerald-300" />
              </span>
              <div className="flex-1">
                <div className="text-[11px] font-semibold uppercase tracking-widest text-emerald-300">
                  Limited offer · Register now
                </div>
                <div className="mt-0.5 text-sm font-semibold text-white">
                  Get started with <span className="text-emerald-300">expert ads</span> for only ₹16/day
                </div>
              </div>
            </div>
          </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="lead-name" className="text-slate-300">Full name</Label>
            <Input
              id="lead-name"
              value={form.name}
              onChange={update("name")}
              placeholder="Your name"
              className="border-white/10 bg-white/5 text-white placeholder:text-slate-500"
              data-testid="lead-name-input"
              required
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="lead-email" className="text-slate-300">Email</Label>
              <Input
                id="lead-email"
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder="Enter your email"
                className="border-white/10 bg-white/5 text-white placeholder:text-slate-500"
                data-testid="lead-email-input"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lead-phone" className="text-slate-300">Phone (WhatsApp)</Label>
              <Input
                id="lead-phone"
                value={form.phone}
                onChange={update("phone")}
                placeholder="+91 99999 99999"
                className="border-white/10 bg-white/5 text-white placeholder:text-slate-500"
                data-testid="lead-phone-input"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="lead-message" className="text-slate-300">Describe your business?</Label>
            <Textarea
              id="lead-message"
              value={form.message}
              onChange={update("message")}
              placeholder="Describe your business activity…"
              rows={3}
              className="border-white/10 bg-white/5 text-white placeholder:text-slate-500"
              data-testid="lead-message-input"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary inline-flex h-11 w-full items-center justify-center gap-2 rounded-full text-sm font-semibold text-white disabled:opacity-60"
            data-testid="lead-submit-btn"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Sending…
              </>
            ) : (
              <>
                <Send className="h-4 w-4" /> Submit request
              </>
            )}
          </button>
          <p className="text-center text-[11px] text-slate-500">
            We respect your privacy. No spam, ever.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
